/**
 * Keeps a login token fresh with the auth API's `refreshjwt` endpoint, so the app can
 * use tokens that expire. A token from a login link has no expiry and is exchanged at
 * once; a refreshed token is renewed when most of its lifetime has passed, or on opening
 * the app if it has expired, which the auth API allows within the session for a grace
 * period after expiry (REFRESH_GRACE_SECONDS, by default 72 hours). When the auth API
 * refuses (401 or 403), the user has to log in again with a link.
 *
 * Off unless REACT_FEATURE_TOKEN_REFRESH is 'true': turning it on logs out every user
 * whose login link is older than the auth API's maximum session (30 days), so it is
 * switched on together with the rotation of the token signing key.
 */

// Renew a token once this fraction of its lifetime has passed, so a failed refresh can
// be retried before it expires
const REFRESH_AT_LIFETIME_FRACTION = 0.75;
// For a token without an issue time, whose lifetime is unknown: renew it this long
// before it expires
const REFRESH_BEFORE_EXPIRY_WITHOUT_IAT_MS = 15 * 60 * 1000;
// When the end of the session is not known: a token living less than this is taken to
// run until the end of its session, since the auth API shortens the last tokens of a
// session, and is not renewed
const MIN_LIFETIME_TO_REFRESH_S = 5 * 60;
// Spreads the refreshes of several tabs of the same user, which share the token, by up
// to this or a tenth of the wait, whichever is less, so the refresh stays before expiry
const MAX_REFRESH_JITTER_MS = 30 * 1000;
// Waits between retries when the auth API cannot be reached: doubles from the first to
// the last, but the last retry before expiry comes at least this long before it, and
// retries are never closer than the minimum
const FIRST_RETRY_DELAY_MS = 15 * 1000;
const MAX_RETRY_DELAY_MS = 10 * 60 * 1000;
const LAST_RETRY_BEFORE_EXPIRY_MS = 30 * 1000;
const MIN_RETRY_DELAY_MS = 5 * 1000;
// A refresh request that takes longer counts as unavailable and is retried
const REFRESH_REQUEST_TIMEOUT_MS = 30 * 1000;
// setTimeout fires at once for delays above 2^31 - 1 ms (about 24.8 days)
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;
// The messages of refreshjwt's own refusals; a 401 or 403 with anything else comes
// from something in front of it and does not log the user out
const AUTH_API_REFUSAL_MESSAGES = ['Token is not valid', 'Token cannot be refreshed'];
// Defines a maximum time for the refresh requests to happen, or undefined for no constraints.
// This can be useful to debug the refresh behaviour locally by forcing it to happen more often.
const AUTH_FORCED_MAX_REFRESH_MS = Number.isFinite(
  Number.parseInt(process.env.REACT_AUTH_FORCED_MAX_REFRESH_MS ?? ''),
)
  ? Number.parseInt(process.env.REACT_AUTH_FORCED_MAX_REFRESH_MS ?? '')
  : undefined;

/**
 * The claims of a token this app needs, as far as they are set. Times are in seconds
 * since epoch.
 */
export type TokenClaims = {
  iat?: number;
  exp?: number;
  // The start of the session, carried over by refreshjwt; unset for a login link token
  authTime?: number;
  email?: string;
};

/**
 * The outcome of asking the auth API for a new token:
 * - refreshed: the new token, and how long until its session ends, in seconds
 * - refused: the token may not be used any more (401 or 403), the user must log in again
 * - unavailable: the auth API could not answer, try again later with the same token
 * - superseded: another tab has already renewed the token, or the user logged out
 */
export type TokenRefreshResult =
  | {kind: 'refreshed'; token: string; sessionExpiresIn?: number}
  | {kind: 'refused'; status: number}
  | {kind: 'unavailable'; reason: string}
  | {kind: 'superseded'};

/**
 * Whether tokens are refreshed. Never when auth is disabled or a token is forced for
 * local development, since there is nothing to refresh.
 */
export const isTokenRefreshEnabled = (): boolean => {
  return (
    process.env.REACT_FEATURE_TOKEN_REFRESH === 'true' &&
    process.env.REACT_DISABLE_AUTH_CHECK !== 'true' &&
    process.env.REACT_APP_FORCED_TOKEN === undefined
  );
};

// Token payloads are UTF-8, e.g. names with å, ä or ö
const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(
    base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='),
  );
  return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
};

/**
 * Decodes the payload of a JWT token without verifying it, which only the APIs
 * can do. Returns undefined if the token cannot be decoded.
 */
export const decodeTokenPayload = (
  token: string,
): Record<string, unknown> | undefined => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return undefined;
  }
  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64Url(parts[1]));
  } catch {
    return undefined;
  }
  return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : undefined;
};

/**
 * Extract the required claims from a JWT token. Returns undefined if the token
 * cannot be decoded.
 *
 * Requires: `iat`, `exp`, `auth_time` and `email`
 */
export const extractTokenClaims = (token: string): TokenClaims | undefined => {
  const payload = decodeTokenPayload(token);
  if (payload === undefined) {
    return undefined;
  }

  const _parseTimestamp = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  const {iat, exp, auth_time, email} = payload;

  return {
    iat: _parseTimestamp(iat),
    exp: _parseTimestamp(exp),
    authTime: _parseTimestamp(auth_time),
    email: typeof email === 'string' && email ? email : undefined,
  };
};

/**
 * When the session of a token started: its `auth_time`, or for a login link token its
 * `iat`, the earlier of the two, as the auth API counts it
 */
export const getTokenSessionStart = (claims: TokenClaims): number | undefined => {
  const times = [claims.authTime, claims.iat].filter(
    (value): value is number => value !== undefined,
  );
  return times.length ? Math.min(...times) : undefined;
};

/**
 * Whether `next` renews `previous`: the same user, a refreshed token that carries the
 * session `previous` belongs to, and issued after it. A renewed token needs no new login
 * check, since the auth API applied the login rules when it issued it.
 */
export const isSameTokenSession = (
  previous: string,
  next: string,
  initiator: string,
): boolean => {
  const previousClaims = extractTokenClaims(previous);
  const nextClaims = extractTokenClaims(next);
  if (!previousClaims?.email || !nextClaims?.email) {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('isSameTokenSession: email differs', {
        initiator,
        prev: previousClaims?.email,
        next: nextClaims?.email,
      });
    }
    return false;
  }
  const start = getTokenSessionStart(previousClaims);
  const check =
    previousClaims.email === nextClaims.email &&
    nextClaims.authTime !== undefined &&
    start !== undefined &&
    nextClaims.authTime === start &&
    previousClaims.iat !== undefined &&
    nextClaims.iat !== undefined &&
    nextClaims.iat > previousClaims.iat;
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('isSameTokenSession:', {
      initiator,
      check,
      sameEmail: previousClaims.email === nextClaims.email,
      nextHasAuthTime: nextClaims.authTime !== undefined,
      hasSessionStart: start !== undefined,
      sameTime: nextClaims.authTime === start,
      prevHasIat: previousClaims.iat !== undefined,
      nextHasIat: nextClaims.iat !== undefined,
      nextIsNewer:
        nextClaims.iat && previousClaims.iat && nextClaims.iat > previousClaims.iat,
    });
  }
  return check;
};

/**
 * Whether a token requires to be exchanged with a new one: a login link token, which
 * never expires, or an expired token, which only refreshjwt still accepts
 */
export const needsTokenExchange = (claims: TokenClaims, serverNowMs: number): boolean => {
  return claims.exp === undefined || claims.exp * 1000 <= serverNowMs;
};

/**
 * What to do with a token, given the auth API's current time in ms (this browser's
 * clock corrected by the offset measured from the last token it received, so a wrong
 * clock cannot make the app refresh in a loop or too late):
 * - refresh after delayMs; a token without an expiry comes from a login link and is
 *   exchanged at once
 * - expire after delayMs: the token runs until its session ends (`sessionEndsAt`, in
 *   seconds, as reported by the auth API; when unknown, a token living less than five
 *   minutes is taken to), so renewing it would not help; the user must then log in again
 */
export const getRefreshPlan = (
  claims: TokenClaims,
  serverNowMs: number,
  sessionEndsAt?: number,
): {action: 'refresh' | 'expire'; delayMs: number} => {
  const {iat, exp} = claims;
  if (exp === undefined) {
    return {action: 'refresh', delayMs: 0};
  }
  const expMs = exp * 1000;
  const endsWithSession =
    sessionEndsAt !== undefined
      ? exp >= sessionEndsAt
      : iat !== undefined && exp - iat < MIN_LIFETIME_TO_REFRESH_S;
  if (console.log) {
    console.log('getRefreshPlan', {
      sessionEndsAt,
      exp,
      iat,
      MIN_LIFETIME_TO_REFRESH_S: MIN_LIFETIME_TO_REFRESH_S,
    });
  }
  if (endsWithSession) {
    const delayMs = expMs - serverNowMs;
    return {
      action: 'expire',
      delayMs: Math.max(
        0,
        AUTH_FORCED_MAX_REFRESH_MS === undefined
          ? delayMs
          : Math.min(delayMs, AUTH_FORCED_MAX_REFRESH_MS),
      ),
    };
  }
  const refreshAtMs =
    iat !== undefined
      ? iat * 1000 + (expMs - iat * 1000) * REFRESH_AT_LIFETIME_FRACTION
      : expMs - REFRESH_BEFORE_EXPIRY_WITHOUT_IAT_MS;
  const delayMs = refreshAtMs - serverNowMs;
  return {
    action: 'refresh',
    delayMs: Math.max(
      0,
      AUTH_FORCED_MAX_REFRESH_MS === undefined
        ? delayMs
        : Math.min(delayMs, AUTH_FORCED_MAX_REFRESH_MS),
    ),
  };
};

/**
 * How long to wait before retry number `retry` (1 for the first) after the auth API
 * could not be reached, in ms. Before the token expires, the wait ends early enough
 * for a last retry while the token is still valid.
 * @param retry The number of the retry
 * @param msToExpiry How long until the token expires, if it has an expiry
 */
export const retryDelayMs = (retry: number, msToExpiry?: number): number => {
  const backoffMs = Math.min(
    MAX_RETRY_DELAY_MS,
    FIRST_RETRY_DELAY_MS * 2 ** Math.max(0, retry - 1),
  );
  if (msToExpiry === undefined || msToExpiry <= 0) {
    return backoffMs;
  }
  return Math.min(
    backoffMs,
    Math.max(MIN_RETRY_DELAY_MS, msToExpiry - LAST_RETRY_BEFORE_EXPIRY_MS),
  );
};

/**
 * Asks the auth API for a new token in exchange for `token`. Never throws. Only the
 * auth API's own refusals log the user out; anything else, including a 404 while the
 * endpoint is not deployed, a stopped app, a gateway page or a token that does not
 * renew this session, is reported as unavailable so the token is kept.
 */
export const requestTokenRefresh = async (
  authApiUrl: string,
  token: string,
  fetchFn: typeof fetch = fetch,
  timeoutMs: number = REFRESH_REQUEST_TIMEOUT_MS,
): Promise<TokenRefreshResult> => {
  const abortController = new AbortController();
  const timeout = setTimeout(() => abortController.abort(), timeoutMs);
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('requestTokenRefresh');
  }
  try {
    let response: Response;
    try {
      // Like the checkjwt request, without a JSON content type, so the browser sends
      // no preflight request
      response = await fetchFn(`${authApiUrl}/refreshjwt`, {
        method: 'POST',
        body: JSON.stringify({token}),
        signal: abortController.signal,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : error;
      return {kind: 'unavailable', reason: `request failed: ${message}`};
    }
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    if (response.status === 401 || response.status === 403) {
      const message = (body as {message?: unknown} | undefined)?.message;
      return typeof message === 'string' && AUTH_API_REFUSAL_MESSAGES.includes(message)
        ? {kind: 'refused', status: response.status}
        : {kind: 'unavailable', reason: `HTTP ${response.status} not from the auth API`};
    }
    if (!response.ok) {
      return {kind: 'unavailable', reason: `HTTP ${response.status}`};
    }
    const {token: refreshed, sessionExpiresIn} = (body ?? {}) as {
      token?: unknown;
      sessionExpiresIn?: unknown;
    };
    if (
      typeof refreshed !== 'string' ||
      extractTokenClaims(refreshed)?.exp === undefined ||
      !isSameTokenSession(token, refreshed, 'requestTokenRefresh')
    ) {
      return {kind: 'unavailable', reason: 'response has no token renewing this session'};
    }
    return {
      kind: 'refreshed',
      token: refreshed,
      sessionExpiresIn:
        typeof sessionExpiresIn === 'number' ? sessionExpiresIn : undefined,
    };
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * What the scheduler needs from its surroundings, so it can be tested without a browser
 */
export type TokenRefreshEnvironment = {
  // Requests a new token and, when one is issued, stores it
  requestRefresh: (token: string) => Promise<TokenRefreshResult>;
  // Called when the token may not be used any more and the user must log in again
  onRefused: () => void;
  now: () => number;
  // This clock minus the auth API's, in ms, as measured from the last token it issued
  clockOffsetMs: () => number;
  // When the session of a token with these claims ends, in seconds, if known
  sessionEndsAt: (claims: TokenClaims) => number | undefined;
  // Between 0 and 1, spreads the refreshes of several tabs
  random: () => number;
  setTimer: (callback: () => void, delayMs: number) => unknown;
  clearTimer: (timer: unknown) => void;
};

const formatMilliseconds = (millis: number): string => {
  const fraction = Math.round(millis) % 1000;
  const seconds = Math.round(Math.round(millis) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  if (hours > 0) {
    return `${hours}:${minutes % 60}:${seconds % 60}.${fraction}`;
  } else if (minutes > 0) {
    return `${minutes}:${seconds % 50}.${fraction}`;
  } else {
    return `${seconds}.${fraction}`;
  }
};

/**
 * Schedules the refresh of `token` and retries it while the auth API cannot be
 * reached. A new token is handled by a new scheduler. Returns:
 * - catchUp: runs a timer whose time has passed on this clock, for when timers were
 *   suspended (a laptop sleeping); cheap, so it can be called every minute
 * - retryNow: retries at once while waiting to retry, e.g. when the network returns
 * - refreshNow: refreshes at once, e.g. when the login check finds the token expired
 *   earlier than planned; failures are then retried as usual
 * - stop: when the token changes or the app unmounts
 */
export const scheduleTokenRefresh = (
  token: string,
  environment: TokenRefreshEnvironment,
): {
  catchUp: () => void;
  retryNow: () => void;
  refreshNow: () => void;
  stop: () => void;
} => {
  const {requestRefresh, onRefused, now, clockOffsetMs, random} = environment;
  const claims = extractTokenClaims(token);
  let timer: unknown = undefined;
  // When the timer is due on this clock, and what it then does
  let dueAtMs = 0;
  let onDue: () => void = () => {};
  let retries = 0;
  let inFlight = false;
  let stopped = false;

  const clear = () => {
    if (timer !== undefined) {
      environment.clearTimer(timer);
      timer = undefined;
    }
  };

  const schedule = (delayMs: number, callback: () => void, reason: string) => {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log(`scheduleTokenRefresh.schedule: reason=${reason}`, {
        delayMs,
        delay: formatMilliseconds(delayMs),
      });
    }
    clear();
    dueAtMs = now() + Math.max(0, delayMs);
    // A timer cannot wait that long: plan again when it fires
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('scheduleTokenRefresh.schedule: onDue', {
        onDue: delayMs > MAX_TIMER_DELAY_MS ? 'plan' : 'callback',
      });
    }
    onDue = delayMs > MAX_TIMER_DELAY_MS ? determinePlan : callback;
    timer = environment.setTimer(
      () => {
        timer = undefined;
        onDue();
      },
      Math.min(Math.max(0, delayMs), MAX_TIMER_DELAY_MS),
    );
  };

  const msToExpiry = () =>
    claims?.exp !== undefined ? claims.exp * 1000 - (now() - clockOffsetMs()) : undefined;

  const retry = () => {
    retries += 1;
    schedule(retryDelayMs(retries, msToExpiry()), run, 'retry');
  };

  const stop = () => {
    stopped = true;
    clear();
  };

  const run = async () => {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('scheduleTokenRefresh.run');
    }
    if (stopped || inFlight) {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('scheduleTokenRefresh.run stopped or inflight');
      }
      return;
    }
    inFlight = true;
    let result: TokenRefreshResult;
    try {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('scheduleTokenRefresh.run: requestRefresh()');
      }
      result = await requestRefresh(token);
    } catch (error) {
      const message = error instanceof Error ? error.message : error;
      result = {kind: 'unavailable', reason: `${message}`};
    } finally {
      inFlight = false;
    }
    if (stopped) {
      return;
    }
    if (result.kind === 'refreshed') {
      // Stopped when the new token is stored; if storing it failed, try again later
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('scheduleTokenRefresh.run retry() due to refreshed');
      }
      retry();
    } else if (result.kind === 'refused') {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('scheduleTokenRefresh.run stop() due to refused');
      }
      stop();
      onRefused();
    } else if (result.kind === 'superseded') {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('scheduleTokenRefresh.run stop() due to superseded');
      }
      stop();
    } else {
      console.warn(`Token refresh failed, retrying: ${result.reason}`);
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('scheduleTokenRefresh.run retry() due to failed');
      }
      retry();
    }
  };

  const expire = () => {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('scheduleTokenRefresh.expire stop() due to expire');
    }
    stop();
    onRefused();
  };

  function determinePlan() {
    if (stopped || claims === undefined) {
      // Not a token this app can renew, leave it to the login check
      return;
    }
    const decision = getRefreshPlan(
      claims,
      now() - clockOffsetMs(),
      environment.sessionEndsAt(claims),
    );
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('scheduleTokenRefresh.determinePlan: refresh plan', {
        action: decision.action,
        now: now(),
        clockOffsetMs: clockOffsetMs(),
      });
    }
    if (decision.action === 'expire') {
      schedule(decision.delayMs, expire, 'expire');
    } else {
      const jitterMs = random() * Math.min(MAX_REFRESH_JITTER_MS, decision.delayMs / 10);
      schedule(decision.delayMs + jitterMs, run, 'refresh');
    }
  }

  const catchUp = () => {
    if (!stopped && !inFlight && timer !== undefined && now() >= dueAtMs) {
      clear();
      onDue();
    }
  };

  const retryNow = () => {
    if (!stopped && !inFlight && retries > 0) {
      clear();
      run();
    }
  };

  determinePlan();
  const refreshNow = () => {
    if (!stopped && !inFlight && claims !== undefined) {
      clear();
      run();
    }
  };

  return {catchUp, retryNow, refreshNow, stop};
};

/**
 * Refreshes a token on behalf of the tabs that share it (see useTokenRefresh): within
 * one lock, so they renew it once, and only while it is still the stored token. If
 * another tab has replaced it, or the user logged out, that is adopted instead. Asking
 * again for a token whose refresh is under way gets the same answer, so the scheduler
 * and the login check share one request.
 */
export const createStoredTokenRefresh = (dependencies: {
  read: () => string | undefined;
  request: (token: string) => Promise<TokenRefreshResult>;
  // Stores a new token, with how long until its session ends, in seconds, if known
  store: (token: string, sessionExpiresIn: number | undefined) => void;
  // Follows the stored token, which another tab changed, without writing it
  adopt: () => void;
  lock: (callback: () => Promise<TokenRefreshResult>) => Promise<TokenRefreshResult>;
}): ((current: string) => Promise<TokenRefreshResult>) => {
  const {
    read: getStoredToken,
    request: requestRefresh,
    store: storeResult,
    adopt: adoptNew,
    lock: withLock,
  } = dependencies;

  // Another tab may have renewed the token, logged out or logged in
  const isStillStored = (current: string) => {
    if (getStoredToken() === current) {
      return true;
    }
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('createStoredTokenRefresh.isStillStored adoptNew()');
    }
    adoptNew();
    return false;
  };

  let pending: {token: string; result: Promise<TokenRefreshResult>} | undefined;
  return (current) => {
    if (pending?.token === current) {
      return pending.result;
    }

    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('createStoredTokenRefresh.function lock start');
    }
    const result = withLock(async () => {
      if (!isStillStored(current)) {
        return {kind: 'superseded'};
      }

      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('createStoredTokenRefresh.function requestRefresh()');
      }
      const refreshResult = await requestRefresh(current);
      if (refreshResult.kind === 'refreshed') {
        if (!isStillStored(current)) {
          return {kind: 'superseded'};
        }

        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log('createStoredTokenRefresh.function storeResult()');
        }
        storeResult(refreshResult.token, refreshResult.sessionExpiresIn);
      }
      return refreshResult;
    });
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('createStoredTokenRefresh.function lock done');
    }

    pending = {token: current, result};
    const settle = () => {
      if (pending?.result === result) {
        pending = undefined;
      }
    };
    result.then(settle, settle);
    return result;
  };
};

// The claims the user state is built from; a renewal that changes none of them leaves
// the user state as it is
const PROFILE_CLAIMS = ['id', 'email', 'group', 'customerId', 'tabAccess', 'trackAccess'];

/**
 * When `token` renews the session of `authenticatedToken`, the token the logged-in user
 * state came from: the renewed profile, and whether it differs from the one the state
 * was built from. A renewed token's claims are the user's current profile, so access
 * changes since the login link take effect. Undefined when `token` is not a renewal and
 * needs a login check.
 */
export const checkForRenewedProfile = (
  authenticatedToken: string | undefined,
  token: string | undefined,
  isAuthenticated: boolean,
): {profile: Record<string, unknown>; changed: boolean} | undefined => {
  if (!token || !authenticatedToken || !isAuthenticated) {
    return undefined;
  }
  if (token === authenticatedToken) {
    const profile = decodeTokenPayload(token);
    if (!profile) {
      return undefined;
    }
    return {profile, changed: false};
  }
  if (!isSameTokenSession(authenticatedToken, token, 'checkForRenewedProfile')) {
    return undefined;
  }
  const profile = decodeTokenPayload(token);
  const previous = decodeTokenPayload(authenticatedToken);
  if (!profile || !previous) {
    return undefined;
  }
  const changed = PROFILE_CLAIMS.some(
    (claim) => JSON.stringify(profile[claim]) !== JSON.stringify(previous[claim]),
  );
  return {profile, changed};
};

// jsonwebtoken's message for an expired token, which checkjwt passes on
export const JWT_EXPIRED_MESSAGE = 'jwt expired';

/**
 * The reason checkjwt refused a token, from the body of its 401 or 403: an object with
 * only a string message. Undefined for any other body, which comes from something in
 * front of the auth API, e.g. a gateway page.
 */
export const checkjwtRefusal = (body: unknown): string | undefined => {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return undefined;
  }
  const {message} = body as {message?: unknown};
  return Object.keys(body).length === 1 && typeof message === 'string'
    ? message
    : undefined;
};
